import React from "react";

/* Shared bits: ScorePill, OpportunityCard, OpportunityBriefRow, FeedbackButtons. */

function ScorePill({ score, tier, mode = "both", size = "sm" }) {
  // mode: "both" (% + tier pill) | "tier" (tier pill only)
  const cls = tier === "high" ? "pill-success" : tier === "medium" ? "pill-accent" : "pill-neutral";
  const label = tier === "high" ? "High match" : tier === "medium" ? "Medium match" : "Low match";

  return (
    <span className="score-block">
      {mode === "both" && (
        <span className="score-pct">{score}%</span>
      )}
      <span className={"pill " + cls}>
        <span
          className="pill-dot"
          style={{
            background:
              tier === "high" ? "var(--success)" :
                tier === "medium" ? "var(--accent)" :
                  "var(--text-quaternary)",
          }}
        />
        {label}
      </span>
    </span>
  );
}

function KindPill({ kind }) {
  return (
    <span className="pill pill-neutral" style={{ textTransform: "capitalize" }}>
      <window.I.Comment size={11} style={{ color: "var(--text-tertiary)" }} />
      {kind}
    </span>
  );
}

function FeedbackButtons({ opp, onFeedback, compact = false }) {
  const [pop, setPop] = React.useState(null);
  const handle = (val, e) => {
    e?.stopPropagation();
    setPop(val);
    onFeedback(opp.id, val);
    setTimeout(() => setPop(null), 360);
  };
  return (
    <div style={{ display: "flex", gap: 6 }}>
      <button
        className={
          "feedback-btn " +
          (opp.feedback === "relevant" ? "is-active positive " : " ") +
          (pop === "relevant" ? "feedback-pop" : "")
        }
        onClick={(e) => handle(opp.feedback === "relevant" ? null : "relevant", e)}
        aria-pressed={opp.feedback === "relevant"}
      >
        <window.I.ThumbsUp size={12} />
        {!compact && "Relevant"}
      </button>
      <button
        className={
          "feedback-btn " +
          (opp.feedback === "not_relevant" ? "is-active negative " : " ") +
          (pop === "not_relevant" ? "feedback-pop" : "")
        }
        onClick={(e) => handle(opp.feedback === "not_relevant" ? null : "not_relevant", e)}
        aria-pressed={opp.feedback === "not_relevant"}
      >
        <window.I.ThumbsDown size={12} />
        {!compact && "Not relevant"}
      </button>
    </div>
  );
}

/* Quick-draft actions surfaced on the card/row footer. Clicking opens the
   opportunity detail with the matching composer (public reply vs. author DM)
   already open, so the user goes straight from feed to draft in one click.
   Generation targets a single post/comment, so this is never rendered on
   comment clusters — there the user picks a member in the detail panel. */
function GenerateActions({ opp, onGenerate, variant = "card" }) {
  if (!onGenerate) return null;
  const fire = (mode, e) => {
    e.stopPropagation();
    onGenerate(opp, mode);
  };
  // PM is only offered when the author is known (mirrors the detail panel):
  // deleted/anonymous accounts have no inbox to message.
  const recipient = String(opp?.authorName || "").trim().replace(/^\/?u\//i, "").trim();
  return (
    <div className={"opp-gen " + (variant === "brief" ? "opp-gen-brief" : "")}>
      <button
        type="button"
        className="opp-gen-btn"
        onClick={(e) => fire("comment", e)}
        title="Draft a public Reddit reply for this opportunity"
      >
        <window.I.Sparkles size={12} />
        Generate comment
      </button>
      {recipient && (
        <button
          type="button"
          className="opp-gen-btn opp-gen-btn-pm"
          onClick={(e) => fire("pm", e)}
          title={`Draft a private message to u/${recipient}`}
        >
          <window.I.Mail size={12} />
          Generate PM
        </button>
      )}
    </div>
  );
}

function tierTagLabel(tier) {
  if (tier === "high") return "HIGH INTENT";
  if (tier === "medium") return "MEDIUM INTENT";
  return "LOW INTENT";
}
function tierTagClass(tier) {
  if (tier === "high") return "tier-high";
  if (tier === "medium") return "tier-med";
  return "tier-low";
}

function OpportunityCard({ opp, onOpen, onFeedback, onGenerate, isSelected, scoreMode = "both" }) {
  const isDimmed = opp.feedback === "not_relevant";
  return (
    <div
      className={"opp-card " + (isSelected ? "is-selected " : "") + (isDimmed ? "is-dimmed " : "")}
      onClick={() => onOpen(opp.id)}
      style={isDimmed ? { opacity: 0.6 } : null}
    >
      <div className="opp-head">
        {opp.isNew && <span className="opp-new-dot" title="New since last scan" />}
        <span className="opp-sub">{opp.subreddit}</span>
        <span className="opp-meta-sep" />
        <KindPill kind={opp.kind} />
        <span className="opp-meta">·  {opp.intent}</span>
        <ScorePill score={opp.score} tier={opp.tier} mode={scoreMode} />
      </div>

      <h3 className="opp-title">{opp.title}</h3>

      <p className="opp-why">{opp.why}</p>

      <div className="opp-tags">
        {opp.intent && opp.intent !== "—" && (
          <span className="pill pill-neutral">
            <window.I.Zap size={11} style={{ color: "var(--text-tertiary)" }} />
            {opp.intent}
          </span>
        )}
        {opp.useCase && opp.useCase !== "—" && (
          <span className="pill pill-neutral">
            <window.I.Target size={11} style={{ color: "var(--text-tertiary)" }} />
            {opp.useCase}
          </span>
        )}
        {opp.persona && (
          <span className="pill pill-neutral">
            <window.I.User size={11} style={{ color: "var(--text-tertiary)" }} />
            {opp.persona}
          </span>
        )}
      </div>

      <div className="opp-footer">
        <div className="opp-footer-stats">
          <span className="opp-footer-stat">
            <window.I.Clock size={12} /> {opp.posted}
          </span>
          <span className="opp-footer-stat">
            <window.I.Comment size={12} /> {opp.comments}
          </span>
          <span className="opp-footer-stat">
            <window.I.Up size={12} /> {opp.upvotes}
          </span>
        </div>
        <GenerateActions opp={opp} onGenerate={onGenerate} />
        {/* <FeedbackButtons opp={opp} onFeedback={onFeedback} /> */}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Editorial "brief row" variant — used on the Dashboard's top list.   */
/* Numbered, generous, with a clear kicker → headline → why → meta.    */
/* ------------------------------------------------------------------ */

function OpportunityBriefRow({ opp, index, onOpen, onFeedback, onGenerate, scoreMode = "both" }) {
  const n = String((index ?? 0) + 1).padStart(2, "0");
  const isDimmed = opp.feedback === "not_relevant";

  return (
    <li
      className={"opp-brief " + (isDimmed ? "is-dimmed " : "")}
      onClick={() => onOpen(opp.id)}
      style={isDimmed ? { opacity: 0.6 } : null}
    >
      <div className="opp-brief-num">
        {n}
      </div>

      <div className="opp-brief-body">
        <div className="opp-brief-kicker">
          <span className="opp-brief-sub">{opp.subreddit}</span>
          <span className="opp-brief-sep">·</span>
          <span className="opp-brief-ago">{opp.posted}</span>
        </div>

        <h3 className="opp-brief-title">{opp.title}</h3>

        <p className="opp-brief-why">{opp.why}</p>

        <div className="opp-brief-foot">
          {scoreMode !== "tier" && (
            <span className="opp-brief-foot-score">{opp.score}% match</span>
          )}
          {scoreMode !== "tier" && <span className="opp-brief-foot-sep">·</span>}
          <span>{opp.comments} comments</span>
          <span className="opp-brief-foot-sep">·</span>
          <span>{opp.upvotes} upvotes</span>
          <GenerateActions opp={opp} onGenerate={onGenerate} variant="brief" />
        </div>
      </div>

      <div className="opp-brief-actions" onClick={(e) => e.stopPropagation()}>
        <span className="opp-brief-arrow" aria-hidden="true">
          <window.I.ArrowUpRight size={14} />
        </span>
      </div>
    </li>
  );
}

/* ------------------------------------------------------------------ */
/* Thread cluster, brief-row variant — used on the Dashboard top-list  */
/* so a thread with N matching comments takes one row, not N. Same     */
/* numbered/kicker/title layout as OpportunityBriefRow but with cluster */
/* affordances (N comments matched pill, no inline feedback — per-     */
/* section feedback lives in the detail panel).                        */
/* ------------------------------------------------------------------ */

function ThreadClusterBriefRow({ opp, index, onOpen, scoreMode = "both" }) {
  const n = String((index ?? 0) + 1).padStart(2, "0");
  const memberCount = opp.memberCount || (opp.members || []).length;
  const threadTitle = opp.threadTitle || opp.redditTitle || opp.title;

  return (
    <li
      className="opp-brief"
      onClick={() => onOpen(opp)}
      style={{ cursor: "pointer" }}
    >
      <div className="opp-brief-num">
        {n}
      </div>

      <div className="opp-brief-body">
        <div className="opp-brief-kicker">
          <span className="opp-brief-sub">{opp.subreddit}</span>
          <span className="opp-brief-sep">·</span>
          <span
            className="pill pill-neutral"
            style={{ textTransform: "none", fontSize: 11 }}
            title={`${memberCount} commenters in this thread match your profile`}
          >
            <window.I.Comment size={10} style={{ color: "var(--text-tertiary)" }} />
            {memberCount} comments matched
          </span>
          <span className="opp-brief-sep">·</span>
          <span className="opp-brief-ago">{opp.posted}</span>
        </div>

        <h3 className="opp-brief-title">
          <span style={{ color: "var(--text-tertiary)", fontWeight: 500 }}>
            Comments on:{" "}
          </span>
          {threadTitle}
        </h3>

        <p className="opp-brief-why">
          {memberCount} {memberCount === 1 ? "commenter" : "commenters"} in
          this thread match your profile. Open to review each one.
        </p>

        <div className="opp-brief-foot">
          {scoreMode !== "tier" && (
            <span className="opp-brief-foot-score">{opp.score}% top match</span>
          )}
          {scoreMode !== "tier" && <span className="opp-brief-foot-sep">·</span>}
          <span>{opp.comments || 0} comments</span>
          <span className="opp-brief-foot-sep">·</span>
          <span>{opp.upvotes || 0} upvotes</span>
        </div>
      </div>

      <div className="opp-brief-actions" onClick={(e) => e.stopPropagation()}>
        {/* No inline feedback on cluster rows — feedback is per-section in
            the detail panel so each commenter gets their own signal. */}
        <span className="opp-brief-arrow" aria-hidden="true">
          <window.I.ArrowUpRight size={14} />
        </span>
      </div>
    </li>
  );
}

/* ------------------------------------------------------------------ */
/* Thread cluster — N comment opportunities living in the same Reddit  */
/* thread, collapsed into a single feed card so the user sees one row  */
/* per thread instead of N near-duplicate cards. Posts stay solo;     */
/* clustering applies only to comments (per product decision).         */
/* ------------------------------------------------------------------ */

function ThreadClusterCard({ opp, onOpen, scoreMode = "both" }) {
  // opp here is a synthetic cluster shape built in clusterOpps():
  //   { id, kind:"comment-cluster", redditPostId, subreddit, threadTitle,
  //     members:[...comment opps], memberCount, tier, score, isNew, posted }
  const memberCount = opp.memberCount || (opp.members || []).length;
  const threadTitle = opp.threadTitle || opp.redditTitle || opp.title;
  return (
    <div
      className="opp-card"
      onClick={() => onOpen(opp)}
      style={{ cursor: "pointer" }}
    >
      <div className="opp-head">
        {opp.isNew && <span className="opp-new-dot" title="New since last scan" />}
        <span className="opp-sub">{opp.subreddit}</span>
        <span className="opp-meta-sep" />
        <span
          className="pill pill-neutral"
          style={{ textTransform: "none" }}
          title={`${memberCount} commenters in this thread match your profile`}
        >
          <window.I.Comment size={11} style={{ color: "var(--text-tertiary)" }} />
          {memberCount} comments matched
        </span>
        <ScorePill score={opp.score} tier={opp.tier} mode={scoreMode} />
      </div>

      <h3 className="opp-title">
        <span style={{ color: "var(--text-tertiary)", fontWeight: 500 }}>
          Comments on:{" "}
        </span>
        {threadTitle}
      </h3>

      <p className="opp-why">
        {memberCount} {memberCount === 1 ? "commenter" : "commenters"} in
        this thread match your profile. Top score{" "}
        <strong>{opp.score}%</strong>. Open to review each one.
      </p>

      <div className="opp-footer">
        <div className="opp-footer-stats">
          <span className="opp-footer-stat">
            <window.I.Clock size={12} /> {opp.posted}
          </span>
          <span className="opp-footer-stat">
            <window.I.Comment size={12} /> {opp.comments || 0}
          </span>
          <span className="opp-footer-stat">
            <window.I.Up size={12} /> {opp.upvotes || 0}
          </span>
        </div>
        <span
          className="opp-meta"
          style={{ fontSize: 12, color: "var(--text-tertiary)" }}
        >
          Review each <window.I.ChevronRight size={11} style={{ verticalAlign: "-2px" }} />
        </span>
      </div>
    </div>
  );
}

/* Pure helper: take a flat opp list and collapse comment opps that share a
   redditPostId into a single synthetic cluster opp. Posts pass through
   unchanged; comments without a redditPostId pass through unchanged; a
   thread with only one matching comment is NOT clustered (no win there).

   The synthetic cluster opp has:
     id           — "cluster:<postId>" (stable for React keys + routing)
     kind         — "comment-cluster" (downstream code branches on this)
     members      — the underlying comment opps, sorted by score desc
     memberCount  — convenience
     score / tier — taken from the highest-scoring member
     isNew        — true if ANY member is new
     threadTitle  — the parent post title (redditTitle of any member)
     posted       — earliest "posted" string in the cluster
     contentItemId / opportunityId — taken from the top member so a single
                                     dismiss/save action has a defined target
*/
function clusterOpps(opps) {
  if (!Array.isArray(opps) || opps.length === 0) return [];
  const out = [];
  const commentsByPost = new Map();
  for (const opp of opps) {
    if (opp.kind === "comment" && opp.redditPostId) {
      const arr = commentsByPost.get(opp.redditPostId) || [];
      arr.push(opp);
      commentsByPost.set(opp.redditPostId, arr);
    } else {
      out.push(opp);
    }
  }
  for (const [postId, rawMembers] of commentsByPost) {
    if (rawMembers.length === 1) {
      out.push(rawMembers[0]);
      continue;
    }
    const members = [...rawMembers].sort((a, b) => b.score - a.score);
    const top = members[0];
    out.push({
      id: `cluster:${postId}`,
      kind: "comment-cluster",
      redditPostId: postId,
      subreddit: top.subreddit,
      threadTitle: top.redditTitle || "Reddit thread",
      title: top.redditTitle || "Reddit thread",
      score: top.score,
      tier: top.tier,
      isNew: members.some((m) => m.isNew),
      posted: top.posted,
      comments: top.comments || 0,
      upvotes: top.upvotes || 0,
      // Inherit a few fields so anything in the codebase that pokes
      // cluster.subreddit / cluster.contentItemId doesn't NPE.
      contentItemId: top.contentItemId,
      opportunityId: top.opportunityId,
      permalink: top.permalink,
      members,
      memberCount: members.length,
    });
  }
  return out;
}

function countOpportunities(items) {
  if (!Array.isArray(items)) return 0;
  let n = 0;
  for (const it of items) {
    if (it.kind === "comment-cluster") {
      n += it.memberCount || (it.members ? it.members.length : 0);
    } else {
      n += 1;
    }
  }
  return n;
}

window.ScorePill = ScorePill;
window.KindPill = KindPill;
window.FeedbackButtons = FeedbackButtons;
window.OpportunityCard = OpportunityCard;
window.OpportunityBriefRow = OpportunityBriefRow;
window.ThreadClusterCard = ThreadClusterCard;
window.ThreadClusterBriefRow = ThreadClusterBriefRow;
window.clusterOpps = clusterOpps;
window.countOpportunities = countOpportunities;
